import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useCityContext } from '@contexts/LocationContext'
import { useAuth } from '@contexts/AuthContext'
import Button from '@components/Button'
import LocationSelector from '@components/LocationSelector'
import './Header.css'

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [showLocationSelector, setShowLocationSelector] = useState(false)
  const { city, isRibeirao } = useCityContext()
  const { user, isAuthenticated, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation() // Get current location

  const isHomePage = location.pathname === `/${city?.slug || ''}` || location.pathname === `/${city?.slug || ''}/`

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen)
  }

  const handleSignOut = async () => {
    await signOut()
    // After sign out, navigate to the city-specific home page, not the root
    navigate(`/${city?.slug || ''}`)
  }

  return (
    <header className="header">
      <div className="header__container container">
        {/* Logo */}
        <Link to={`/${city?.slug || ''}`} className="header__logo">
          <img 
            src={isRibeirao ? '/icon-rib.png' : '/icon-udi.png'} 
            alt="Arqzin Ingressos" 
            className="header__logo-image" 
          />
        </Link>

        {/* Centered Mobile City Selector */}
        {city && (
          <button 
            className="header__city-badge header__city-badge--mobile"
            onClick={() => setShowLocationSelector(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span>{city.name.includes('Ribeirão') ? 'Ribeirão' : city.name}</span>
          </button>
        )}

        {/* Navigation Desktop */}
        <nav className="header__nav header__nav--desktop">
          {!isHomePage && (
            <Link to={`/${city?.slug || ''}`} className="header__nav-link">
              Eventos
            </Link>
          )}
          <button 
            className="header__nav-city-display"
            onClick={() => setShowLocationSelector(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {city?.name || 'Carregando cidade...'}
          </button>
        </nav>

        {/* Actions Desktop */}
        <div className="header__actions header__actions--desktop">
          {isAuthenticated ? (
            <>
              <Link to={`/${city?.slug || ''}/carteira`} className="header__nav-link">
                Carteira
              </Link>
              <div className="header__user-menu">
                <button className="header__user-btn">
                  <div className="header__user-avatar">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.name} />
                    ) : (
                      <span>{user?.name?.charAt(0) || 'U'}</span>
                    )}
                  </div>
                </button>
                <div className="header__dropdown">
                  <Link to={`/${city?.slug || ''}/perfil`} className="header__dropdown-item">
                    Meu Perfil
                  </Link>
                  <button onClick={handleSignOut} className="header__dropdown-item header__dropdown-item--danger">
                    Sair
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <Link to={`/${city?.slug || ''}/login`} state={{ from: location }}>
                <Button variant="ghost">Login</Button>
              </Link>
              <Link to={`/${city?.slug || ''}/cadastro`} state={{ from: location }}>
                <Button variant="primary">Cadastrar</Button>
              </Link>
            </>
          )}
        </div>

        {/* Menu Toggle Mobile */}
        <button 
          className={`header__menu-toggle ${menuOpen ? 'header__menu-toggle--open' : ''}`}
          onClick={handleMenuToggle}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Mobile Menu */}
        <div className={`header__mobile-menu ${menuOpen ? 'header__mobile-menu--open' : ''}`}>
          <nav className="header__mobile-nav">
            {!isHomePage && (
              <Link to={`/${city?.slug || ''}`} className="header__mobile-link" onClick={() => setMenuOpen(false)}>
                Eventos
              </Link>
            )}
            <button 
              className="header__mobile-city-display"
              onClick={() => {
                setShowLocationSelector(true)
                setMenuOpen(false)
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>{city?.name || 'Carregando cidade...'}</span>
            </button>
            
            {isAuthenticated ? (
              <>
                <Link to={`/${city?.slug || ''}/carteira`} className="header__mobile-link" onClick={() => setMenuOpen(false)}>
                  Carteira
                </Link>
                <Link to={`/${city?.slug || ''}/perfil`} className="header__mobile-link" onClick={() => setMenuOpen(false)}>
                  Meu Perfil
                </Link>
                <button 
                  onClick={() => {
                    handleSignOut()
                    setMenuOpen(false)
                  }} 
                  className="header__mobile-link header__mobile-link--danger"
                >
                  Sair
                </button>
              </>
            ) : (
              <div className="header__mobile-actions">
                <Link to={`/${city?.slug || ''}/login`} state={{ from: location }} onClick={() => setMenuOpen(false)}>
                  <Button variant="secondary" fullWidth>Login</Button>
                </Link>
                <Link to={`/${city?.slug || ''}/cadastro`} state={{ from: location }} onClick={() => setMenuOpen(false)}>
                  <Button variant="primary" fullWidth>Cadastrar</Button>
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Location Selector Modal */}
      {showLocationSelector && (
        <LocationSelector onClose={() => setShowLocationSelector(false)} />
      )}
    </header>
  )
}

export default Header
